import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { AuthenticationContext } from '@vs-online-only-app/state';
import EnterInviteCode from './steps/invite-code/EnterInviteCode';
import EnterEmail from './steps/email/EnterEmail';
import EnterPassword from './steps/password/EnterPassword';

type CreateAccountState = {
  currentStepIndex: number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  canGoToPreviousStep: boolean;
  currentStep: CreateAccountStep;
  inviteCode: string;
  setInviteCode: (code: string) => void;
  email: string;
  setEmail: (email: string) => void;
  createAccount: (
    password: string,
    passwordConfirmation: string,
  ) => Promise<boolean>;
};

const CreateAccountContext = createContext<CreateAccountState>(
  {} as CreateAccountState,
);

type CreateAccountStep = {
  title: string;
  subtitle: string;
  StepBody: FC;
};

const CREATE_ACCOUNT_STEPS: CreateAccountStep[] = [
  {
    title: 'Enter your invite code',
    subtitle: 'This helps us find your retailer.',
    StepBody: EnterInviteCode,
  },
  {
    title: 'Enter your work email',
    subtitle: "If you don't have one, you can use your personal email.",
    StepBody: EnterEmail,
  },
  {
    title: 'Create a password',
    subtitle:
      'Your password should have at least 8 characters or more and at least one number or special character.',
    StepBody: EnterPassword,
  },
];

const CreateAccountProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  const { registerAssociate } = useContext(AuthenticationContext);

  const getStep = (index: number) => CREATE_ACCOUNT_STEPS[index];

  const currentStep = getStep(currentStepIndex);
  const canGoToPreviousStep = currentStepIndex > 0;

  const goToNextStep = useCallback(() => {
    if (CREATE_ACCOUNT_STEPS.length === currentStepIndex + 1) return;
    setCurrentStepIndex((prevIndex) => prevIndex + 1);
  }, [currentStepIndex]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex === 0) return;
    setCurrentStepIndex((prevIndex) => prevIndex - 1);
  }, [currentStepIndex]);

  const createAccount = useCallback(
    async (password: string, passwordConfirmation: string) => {
      if (!inviteCode || !email || !password || !passwordConfirmation) {
        return false;
      }

      return await registerAssociate({
        code: inviteCode,
        email,
        password,
        passwordConfirmation: password,
      });
    },
    [email, inviteCode, registerAssociate],
  );

  const value = useMemo(
    () => ({
      currentStepIndex,
      goToNextStep,
      goToPreviousStep,
      currentStep,
      canGoToPreviousStep,
      inviteCode,
      setInviteCode,
      email,
      setEmail,
      createAccount,
    }),
    [
      canGoToPreviousStep,
      createAccount,
      currentStep,
      currentStepIndex,
      email,
      goToNextStep,
      goToPreviousStep,
      inviteCode,
    ],
  );

  return (
    <CreateAccountContext.Provider value={value}>
      {children}
    </CreateAccountContext.Provider>
  );
};

export { CreateAccountProvider, CreateAccountContext };
